//
import { Box, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  ReferenceArea,
} from "recharts";
import { useId } from "react";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

export default function LineChartComponent({
  maxValuebps = 0,
  minValuebps = 0,
  isZoomEnabled = false, // Ativa/desativa o zoom
  onZoomChange = (range) => {}, // Callback: (range) => console.log(range)
  data = [],
  customTooltip = null,
  yOptions = {},
  xOptions = {},
  gridOptions = {},
  lineChartOptions = {},
  areaChartOptions = {},
  areaOptions = {},
  mainColor = null,
  mainLineColor = null,
  referenceLines = [], //{name,value,opt}
  lastTickRef = 0.95,
  objectColorsLine = false,
  stackedGraphColors = ["orange", "green", "blue", "red", "yellow"],
  stackedGraphKeys = [],
  formatValue = null,
}) {
  const [refWidth, setRefWidth] = useState(null);
  const [refAreaLeft, setRefAreaLeft] = useState(null);
  const [refAreaRight, setRefAreaRight] = useState(null);
  const M_STYLE = 300;
  const chartId = useId().replace(/[^a-zA-Z0-9]/g, ""); // Remove caracteres inválidos para IDs SVG
  const containerRef = useRef(null);
  const [maxTicks, setMaxTicks] = useState(10);
  const theme = useTheme();
  const MAIN_COLOR = mainColor || theme.palette.cardGraphic.graphColor.blue;
  const MAIN_LINE_COLOR =
    mainLineColor || theme.palette.cardGraphic.graphLineColor.blue;

  const formatBytes =
    typeof formatValue === "function" ? formatValue : defaultFormatBytes;

  const handleMouseDown = (e) => {
    // Adicionei !e.activeLabel para impedir clique fora da área de plotagem (ex: eixos)
    if (!isZoomEnabled || !e || !e.activeLabel) return;
    setRefAreaLeft(e.activeLabel);
  };

  const handleMouseMove = (e) => {
    // Se o mouse sair da área de dados (activeLabel null), ignoramos ou mantemos o último
    if (!isZoomEnabled || !refAreaLeft || !e || !e.activeLabel) return;
    setRefAreaRight(e.activeLabel);
  };

  const handleMouseUp = () => {
    if (!isZoomEnabled) return;

    if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight) {
      // Garante que a data inicial seja menor que a final (caso o user arraste da direita pra esquerda)
      const [start, end] =
        refAreaLeft < refAreaRight
          ? [refAreaLeft, refAreaRight]
          : [refAreaRight, refAreaLeft];

      // Envia os timestamps para o componente pai
      onZoomChange({ start, end });
    }

    // Limpa a área visual de seleção
    setRefAreaLeft(null);
    setRefAreaRight(null);
  };

  const calcInterval = () => {
    if (!maxTicks || maxTicks < 1 || data.length < 2 || data.length < maxTicks)
      return undefined;
    const effectiveMaxTicks = Math.max(2, maxTicks);
    const adjustedLength = Math.floor(data.length * lastTickRef);
    return Math.max(1, Math.floor(adjustedLength / effectiveMaxTicks));
  };

  useEffect(() => {
    const updateTicks = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setRefWidth(width);
        const calculatedTicks = Math.floor(width / 80);
        setMaxTicks(Math.max(2, calculatedTicks));
      }
    };

    updateTicks();
    window.addEventListener("resize", updateTicks);
    return () => window.removeEventListener("resize", updateTicks);
  }, []);

  const mergedYOptions = {
    tickFormatter: formatBytes,
    interval: 0,
    domain: [minValuebps, maxValuebps],
    tick: {
      fill:
        theme.palette.mode === "dark"
          ? theme.palette.white.main
          : theme.palette.black.main,
      opacity: 0.7,
    },
    style: { fontSize: "10px", fontWeight: 500 },
    ...yOptions,
  };

  const mergedXOptions = {
    dataKey: "clock",
    tickFormatter: (clock) => dayjs.unix(clock).tz(timeZone).format("DD/MM"),
    style: { fontSize: "10px", fontWeight: 500 },
    tick: {
      fill:
        theme.palette.mode === "dark"
          ? theme.palette.white.main
          : theme.palette.black.main,
      opacity: 0.7,
    },
    ...xOptions,
  };

  const mergedGridOptions = {
    stroke: MAIN_LINE_COLOR,
    strokeOpacity: 0.5,
    vertical: false,
    strokeDasharray: "3",
    ...gridOptions,
  };

  const mergedAreaChartOptions = {
    margin: {
      top: 20,
      right: 30,
      left: 10,
      bottom: 0,
      ...(refWidth <= M_STYLE && {
        top: 8,
        right: 10,
        left: 4,
      }),
    },
    ...areaChartOptions,
  };

  const mergedLineChartOptions = {
    margin: {
      top: 10,
      left: -20,
      bottom: 0,
    },
    ...lineChartOptions,
  };

  const mergedAreaOptions = {
    type: "monotone",
    dataKey: "value",
    name: "Tráfego",
    strokeWidth: 1,
    stroke: MAIN_COLOR,
    fill: `url(#default-gradient-${chartId})`,
    fillOpacity: 1,
    stackId: 1,
    dot: false,
    ...areaOptions,
  };

  const mergedLineOptions = {
    dot: false,
    type: "monotone",
    ...areaOptions,
  };

  const DefaultToolTip = ({ active, payload, label }) => {
    const theme = useTheme();
    const lines = 3;

    if (!active || !payload?.length) return null;

    const seriesOrder = Object.keys(data?.[0] || {}).filter((key) =>
      stackedGraphKeys.length > 0
        ? key !== "clock" && stackedGraphKeys.some((k) => key.includes(k))
        : key !== "clock",
    );

    const orderedPayload = [...payload].sort(
      (a, b) => seriesOrder.indexOf(a.dataKey) - seriesOrder.indexOf(b.dataKey),
    );

    const visibleItems = [...orderedPayload]
      .reverse()
      .slice(0, lines < orderedPayload.length ? lines - 1 : lines);

    const otherItems = [...orderedPayload]
      .reverse()
      .slice(lines < orderedPayload.length ? lines - 1 : lines);

    const otherSum = otherItems.reduce(
      (sum, item) => sum + (item?.value || 0),
      0,
    );

    return (
      <Box
        sx={{
          bgcolor: theme.palette.paletteCharts.lineCharts,
          padding: "8px",
          borderRadius: "8px",
          boxShadow: `0px 2px 4px ${theme.palette.colors.black.darker}33`,
        }}
      >
        <Typography fontSize="14px" fontWeight={500} mb={1}>
          {`Data: ${dayjs.unix(label).tz(timeZone).format("DD/MM HH:mm")}`}
        </Typography>

        {visibleItems.map((item) => (
          <Stack
            key={item.dataKey}
            direction="row"
            alignItems="center"
            gap={0.5}
          >
            <Box
              sx={{
                bgcolor: item.color || MAIN_COLOR,
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                border: `1px solid ${item.color || MAIN_LINE_COLOR}`,
              }}
            />
            <Typography fontSize="14px">
              {`${
                orderedPayload.length > 1
                  ? item.dataKey?.match(/\[(.*?)\]/)?.[1] || item.dataKey
                  : "Tráfego"
              }: ${formatBytes(item.value)}`}
            </Typography>
          </Stack>
        ))}

        {otherItems.length > 0 && (
          <Stack direction="row" alignItems="center">
            <Box
              sx={{
                bgcolor: MAIN_COLOR,
                width: "12px",
                height: "12px",
                borderRadius: "2px",
                border: `1px solid ${MAIN_COLOR}`,
              }}
            />
            <Typography fontSize="14px">
              {`Outros: ${formatBytes(otherSum)}`}
            </Typography>
          </Stack>
        )}

        {orderedPayload.length > 1 && (
          <Typography fontSize="14px" mt={1} fontWeight={500}>
            {`Total: ${formatBytes(
              orderedPayload.reduce((sum, item) => sum + (item?.value || 0), 0),
            )}`}
          </Typography>
        )}
      </Box>
    );
  };

  const resolveStackedColor = (theme, colorConfig, idx, type = "fill") => {
    if (typeof colorConfig === "string") {
      if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(colorConfig)) {
        return colorConfig;
      }
      return type === "stroke"
        ? theme?.palette?.cardGraphic?.graphLineColor?.[colorConfig]
        : theme?.palette?.cardGraphic?.graphColor?.[colorConfig];
    }

    if (typeof colorConfig === "object") {
      const { palette, color, shade } = colorConfig;
      return theme?.palette?.[palette]?.[color]?.[shade];
    }

    // fallback seguro
    const fallbackColor = stackedGraphColors[idx % stackedGraphColors.length];
    if (typeof fallbackColor === "string" && /^#/.test(fallbackColor)) {
      return fallbackColor;
    }
    return type === "stroke"
      ? theme?.palette?.cardGraphic?.graphLineColor?.[fallbackColor] ||
          MAIN_LINE_COLOR
      : theme?.palette?.cardGraphic?.graphColor?.[fallbackColor] || MAIN_COLOR;
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      <ResponsiveContainer ref={containerRef} width="100%" height="100%">
        {objectColorsLine ? (
          <LineChart
            data={data}
            {...mergedLineChartOptions}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{ cursor: isZoomEnabled ? "crosshair" : "default" }}
          >
            {customTooltip ? (
              <Tooltip content={customTooltip} />
            ) : (
              <Tooltip content={DefaultToolTip} />
            )}
            <CartesianGrid {...mergedGridOptions} />
            <XAxis interval={calcInterval()} {...mergedXOptions} />
            <YAxis {...mergedYOptions} />
            {Object.entries(objectColorsLine).map(([element, color]) => (
              <Line
                key={element}
                dataKey={element}
                stroke={color}
                {...mergedLineOptions}
              />
            ))}
            {isZoomEnabled && refAreaLeft && refAreaRight && (
              <ReferenceArea
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.3}
                fill={theme.palette.primary.main}
                fillOpacity={0.3}
              />
            )}
          </LineChart>
        ) : (
          <AreaChart
            data={data}
            {...mergedAreaChartOptions}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <defs>
              {/* Gradiente padrão para modo simples */}
              <linearGradient
                id={`default-gradient-${chartId}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="60%"
                  stopColor={MAIN_LINE_COLOR}
                  stopOpacity={0.5}
                />
                <stop
                  offset="100%"
                  stopColor={MAIN_COLOR}
                  stopOpacity={theme.palette.mode === "dark" ? 0 : 0.2}
                />
              </linearGradient>

              {/* Gradientes para modo empilhado */}
              {Object.keys(data[0] || {})
                .filter((key) =>
                  stackedGraphKeys.length > 0
                    ? key !== "clock" &&
                      stackedGraphKeys.some((k) => key.includes(k))
                    : key !== "clock",
                )
                .map((key, idx) => (
                  <linearGradient
                    key={key}
                    id={`gradient-${chartId}-${idx}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="60%"
                      stopColor={resolveStackedColor(
                        theme,
                        stackedGraphColors[idx % stackedGraphColors.length],
                        idx,
                        "stroke",
                      )}
                      stopOpacity={0.5}
                    />
                    <stop
                      offset="100%"
                      stopColor={resolveStackedColor(
                        theme,
                        stackedGraphColors[idx % stackedGraphColors.length],
                        idx,
                        "fill",
                      )}
                      stopOpacity={theme.palette.mode === "dark" ? 0 : 0.2}
                    />
                  </linearGradient>
                ))}
            </defs>

            <CartesianGrid {...mergedGridOptions} />
            <XAxis interval={calcInterval()} {...mergedXOptions} />
            <YAxis {...mergedYOptions} />
            {customTooltip ? (
              <Tooltip
                cursor={{
                  stroke: theme.palette.text.secondary,
                  strokeWidth: 1,
                }}
                wrapperStyle={{ zIndex: 10000, outline: "none" }}
                content={customTooltip}
              />
            ) : (
              <Tooltip
                cursor={{
                  stroke: theme.palette.text.secondary,
                  strokeWidth: 1,
                }}
                wrapperStyle={{ zIndex: 10000, outline: "none" }}
                content={DefaultToolTip}
              />
            )}

            {/* Renderização condicional */}
            {Array.isArray(data) &&
              (data.length === 0 || data[0]?.value !== undefined ? (
                // Modo simples: apenas uma série chamada "value"
                <Area {...mergedAreaOptions} />
              ) : (
                // Modo empilhado: múltiplas séries
                Object.keys(data[0] || {})
                  .filter((key) =>
                    stackedGraphKeys.length > 0
                      ? key !== "clock" &&
                        stackedGraphKeys.some((k) => key.includes(k))
                      : key !== "clock",
                  )
                  .map((key, idx) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stackId="1"
                      stroke={resolveStackedColor(
                        theme,
                        stackedGraphColors[idx % stackedGraphColors.length],
                        idx,
                        "stroke",
                      )}
                      fill={`url(#gradient-${chartId}-${idx})`}
                      fillOpacity={0.6}
                    />
                  ))
              ))}

            {referenceLines?.map((line, index) => (
              <ReferenceLine
                key={`ref-line-${index}`}
                stroke={line.opt?.stroke || "red"}
                strokeWidth={line.opt?.strokeWidth || 1.5}
                y={line.value}
                label={{
                  value: line.name,
                  fill: line.opt?.label?.fill || "white",
                  fontSize: line.opt?.label?.fontSize || "12px",
                  fontWeight: line.opt?.label?.fontWeight || 500,
                  position: line.opt?.label?.position || "insideRight",
                  color: line.opt?.label?.color || theme.palette.primary.main,
                  ...line.opt?.label,
                }}
                {...line.opt}
              />
            ))}

            {isZoomEnabled && refAreaLeft && refAreaRight && (
              <ReferenceArea
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.3}
                fill={theme.palette.primary.main} // Cor do destaque
              />
            )}
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

const defaultFormatBytes = (bps) => {
  if (bps === 0) return "0 bps";
  const k = 1000;
  const sizes = ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];
  const i = Math.max(0, Math.floor(Math.log(bps) / Math.log(k)));
  const value = bps / Math.pow(k, i);
  return `${value.toFixed(2)} ${sizes[i]}`;
};
